import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  CustomerServiceOutlined,
  PlusOutlined,
  ProjectOutlined,
  ReloadOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  commentMySupportTicket,
  createMySupportTicket,
  getMySupportTicket,
  getMySupportTickets,
  reopenMySupportTicket,
  resolveMySupportTicket,
  TICKET_CATEGORIES,
  TICKET_STATUS_COLORS,
} from '../modules/support-tickets/api';
import TicketsBoard from '../components/TicketsBoard';

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm') : '—');

/**
 * OVO-117 — the partner portal's Support page (/partner/tickets). List of the
 * partner's tickets, a create modal, and a detail drawer with the conversation
 * (reply + reopen). Everything arrives pre-sanitized by the viewer serializer —
 * this page never sees internal tickets, internal notes or driver identity.
 */
const PartnerTicketsPage = ({ embedded = false }) => {
  const { t } = useTranslation(['routes', 'common']);
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [view, setView] = useState('list');
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { response } = await getMySupportTickets({ take: 20, skip: (p - 1) * 20 });
      setRows(response?.items || []);
      setTotal(response?.total || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { response } = await getMySupportTicket(id);
      if (response?.id) setDetail(response);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitCreate = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { response } = await createMySupportTicket({
        subject: values.subject,
        category: values.category,
        description: values.description || undefined,
      });
      if (response?.id) {
        messageApi.success(t('tickets.created', { id: response.id }));
        form.resetFields();
        setCreateOpen(false);
        load(1);
        setPage(1);
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setSaving(false);
    }
  };

  const submitReply = async () => {
    if (!reply.trim() || !detail) return;
    setSaving(true);
    try {
      const { response } = await commentMySupportTicket(detail.id, reply.trim());
      if (response?.id) {
        setReply('');
        openDetail(detail.id);
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { response } = await reopenMySupportTicket(detail.id);
      if (response?.id) {
        setDetail(response);
        load(page);
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const resolve = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { response } = await resolveMySupportTicket(detail.id);
      if (response?.id) {
        setDetail(response);
        load(page);
        messageApi.success(t('tickets.resolvedDone'));
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 70, render: (id) => `#${id}` },
    {
      title: t('tickets.status'),
      dataIndex: 'status',
      width: 120,
      render: (s) => <Tag color={TICKET_STATUS_COLORS[s]}>{t(`tickets.statuses.${s}`, s)}</Tag>,
    },
    {
      title: t('tickets.category'),
      dataIndex: 'category',
      width: 170,
      render: (c) => <Tag>{t(`tickets.categories.${c}`, c)}</Tag>,
    },
    { title: t('tickets.subject'), dataIndex: 'subject' },
    {
      title: t('tickets.order'),
      width: 220,
      render: (_, rec) =>
        rec.order ? (
          <div>
            <Link to={`/partner/orders/${rec.order.orderCode}`} onClick={(e) => e.stopPropagation()}>
              {rec.order.orderCode}
            </Link>
            {rec.order.deliveryName && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rec.order.deliveryName}</div>
            )}
          </div>
        ) : (
          rec.orderCode || '—'
        ),
    },
    {
      title: t('tickets.lastActivity'),
      dataIndex: 'updatedAt',
      width: 150,
      render: fmtTime,
    },
  ];

  const canReopen = detail && (detail.status === 'RESOLVED' || detail.status === 'WONT_FIX');
  const canResolve = detail && (detail.status === 'OPEN' || detail.status === 'IN_PROGRESS');

  return (
    <div className="PartnerTicketsPage" style={embedded ? {} : { padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {contextHolder}
      <Card
        title={
          <span>
            <CustomerServiceOutlined style={{ marginRight: 8 }} />
            {t('tickets.portalTitle')}
          </span>
        }
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { value: 'list', label: t('tickets.viewList'), icon: <UnorderedListOutlined /> },
                { value: 'board', label: t('tickets.viewBoard'), icon: <ProjectOutlined /> },
              ]}
            />
            {view === 'list' && <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(page)} />}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('tickets.newTicket')}
            </Button>
          </div>
        }
      >
        <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('tickets.portalSubtitle')}</div>
        {view === 'board' && (
          <TicketsBoard
            readOnly
            onOpen={openDetail}
            fetcher={(status, skip) =>
              getMySupportTickets({ status, take: 100, skip }).then(
                ({ response }) => response || { items: [], total: 0 },
              )
            }
          />
        )}
        {view === 'list' && (
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={rows}
            onRow={(rec) => ({ onClick: () => openDetail(rec.id), style: { cursor: 'pointer' } })}
            pagination={{
              current: page,
              pageSize: 20,
              total,
              showSizeChanger: false,
              onChange: (p) => {
                setPage(p);
                load(p);
              },
            }}
          />
        )}
      </Card>

      <Modal
        title={t('tickets.createTitle')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        okText={t('tickets.create')}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ category: 'GENERAL' }}>
          <Form.Item
            name="subject"
            label={t('tickets.subject')}
            rules={[{ required: true, message: t('tickets.subjectRequired') }]}
          >
            <Input maxLength={200} placeholder={t('tickets.subjectPlaceholder')} />
          </Form.Item>
          <Form.Item name="category" label={t('tickets.category')} rules={[{ required: true }]}>
            <Select
              options={TICKET_CATEGORIES.filter((c) => c !== 'COMMENT').map((c) => ({
                value: c,
                label: t(`tickets.categories.${c}`, c),
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label={t('tickets.description')}>
            <Input.TextArea rows={4} maxLength={5000} placeholder={t('tickets.portalDescriptionPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detail ? `#${detail.id} — ${detail.subject}` : ''}
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
      >
        {detailLoading && <Spin />}
        {detail && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tag color={TICKET_STATUS_COLORS[detail.status]}>
                {t(`tickets.statuses.${detail.status}`, detail.status)}
              </Tag>
              <Tag>{t(`tickets.categories.${detail.category}`, detail.category)}</Tag>
              {!detail.order && detail.orderCode && <Tag>{detail.orderCode}</Tag>}
              {canResolve && (
                <Popconfirm title={t('tickets.markResolvedConfirm')} onConfirm={resolve}>
                  <Button size="small" type="primary" icon={<CheckOutlined />} loading={saving}>
                    {t('tickets.markResolved')}
                  </Button>
                </Popconfirm>
              )}
              {canReopen && (
                <Button size="small" loading={saving} onClick={reopen}>
                  {t('tickets.reopen')}
                </Button>
              )}
            </div>
            {detail.order && (
              <Card
                size="small"
                style={{ marginTop: 12 }}
                title={
                  <Link to={`/partner/orders/${detail.order.orderCode}`}>
                    {detail.order.orderCode} — {t('tickets.openOrder')}
                  </Link>
                }
              >
                <Descriptions size="small" column={1}>
                  {detail.order.partnerOrderRef && (
                    <Descriptions.Item label={t('tickets.orderPartnerRef')}>
                      {detail.order.partnerOrderRef}
                    </Descriptions.Item>
                  )}
                  {detail.order.deliveryDate && (
                    <Descriptions.Item label={t('tickets.orderDate')}>
                      {dayjs(detail.order.deliveryDate).format('DD MMM YYYY')}
                    </Descriptions.Item>
                  )}
                  {detail.order.pickupName && (
                    <Descriptions.Item label={t('tickets.orderPickup')}>{detail.order.pickupName}</Descriptions.Item>
                  )}
                  {(detail.order.deliveryName || detail.order.deliveryAddress) && (
                    <Descriptions.Item label={t('tickets.orderDelivery')}>
                      {[detail.order.deliveryName, detail.order.deliveryAddress].filter(Boolean).join(' — ')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
            {detail.description && (
              <Card size="small" style={{ marginTop: 12, background: 'var(--surface-2)' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{detail.description}</div>
              </Card>
            )}
            <h4 style={{ marginTop: 16 }}>{t('tickets.conversation')}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(detail.comments || []).length === 0 && (
                <div style={{ color: 'var(--text-muted)' }}>{t('tickets.noComments')}</div>
              )}
              {(detail.comments || []).map((c) => (
                <div key={c.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: '6px 10px' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    <b>{c.authorRole === 'PARTNER_VIEWER' ? c.author?.name || t('tickets.you') : 'OVO'}</b>
                    {' · '}
                    {fmtTime(c.createdAt)}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Input.TextArea
                rows={3}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={t('tickets.replyPlaceholderPartner')}
                maxLength={5000}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                style={{ marginTop: 8 }}
                loading={saving}
                disabled={!reply.trim()}
                onClick={submitReply}
              >
                {t('tickets.send')}
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default PartnerTicketsPage;
